// Display rules for the contact page: what each email in the history says
// about itself, where each list stands, and a few totals. Pure (no React) so
// they're unit-tested.

export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? String(value)
    : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export const formatCustom = (value, fieldType) => {
  if (value === null || value === undefined || value === "") return "—";
  if (fieldType === "boolean") return value === true || value === "true" ? "Yes" : "No";
  if (fieldType === "date") return formatDate(value);
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};

const KIND_LABEL = { campaign: "Campaign", broadcast: "Broadcast", sequence: "Sequence" };

// Where the email's source lives in the app (null if it was deleted).
export function sourcePath(email) {
  if (!email.source_id) return null;
  switch (email.kind) {
    case "campaign": return `/signups/${email.source_id}/view`;
    case "broadcast": return `/broadcasts/${email.source_id}/view`;
    case "sequence": return `/sequences/${email.source_id}/edit`;
    default: return null;
  }
}

// emailWhat names the email: its source ("Lone Star", or "a deleted
// campaign") and which of that source's emails it was.
export function emailWhat(email) {
  const kind = KIND_LABEL[email.kind] || "Email";
  const source = email.source_name || `a deleted ${kind.toLowerCase()}`;
  let detail = "";
  if (email.kind === "campaign") {
    detail = email.detail === "followup" ? "follow-up email" : "signup email";
  } else if (email.kind === "sequence") {
    const step = email.step_order != null ? `step ${email.step_order + 1}` : "a step";
    detail = email.detail ? `${step}: ${email.detail}` : step;
  } else if (email.detail) {
    detail = email.detail;
  }
  return { kind, source, detail };
}

export const totalClicks = (email) =>
  Object.values(email.clicks || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);

// emailResults is what happened to the email, in order, as tags. An email
// with no send date never went out (a send that failed).
export function emailResults(email) {
  if (!email.send_date) {
    return [{ label: "Not sent", color: "red", note: "This email was never sent: the send failed." }];
  }
  const out = [{ label: "Sent", color: "gray" }];
  if (email.open_date) out.push({ label: "Opened", color: "arcoblue" });
  const clicks = totalClicks(email);
  if (clicks > 0) {
    const links = Object.entries(email.clicks).map(([link, n]) => `${link} (${n})`).join(", ");
    out.push({ label: clicks === 1 ? "Clicked" : `Clicked ${clicks}×`, color: "cyan", note: "Links: " + links });
  }
  if (email.confirm_date) out.push({ label: "Confirmed", color: "green" });
  if (email.unsubscribe_date) out.push({ label: "Unsubscribed", color: "orangered" });
  return out;
}

// listStatus says where the contact stands on one list.
export function listStatus(subscription) {
  if (subscription.unsubscribe_date) return { label: "Unsubscribed", color: "gray" };
  if (!subscription.subscribe_date) return { label: "Pending", color: "orange", note: "Hasn't confirmed yet" };
  return { label: "Active", color: "green" };
}

// signedUpVia is the campaign whose email reached them first — how they most
// likely signed up. Null when no campaign email is in the history.
export function signedUpVia(emails) {
  const campaign = (emails || []).filter((e) => e.kind === "campaign");
  if (!campaign.length) return null;
  const first = campaign.reduce((a, b) => (new Date(a.created_date) <= new Date(b.created_date) ? a : b));
  return { name: first.source_name || "a deleted campaign", path: sourcePath(first), date: first.created_date };
}

// engagement totals the history: emails sent, opened, clicked, never sent.
export function engagement(emails) {
  const list = emails || [];
  const sent = list.filter((e) => e.send_date);
  return {
    sent: sent.length,
    opened: sent.filter((e) => e.open_date).length,
    clicked: sent.filter((e) => totalClicks(e) > 0).length,
    notSent: list.length - sent.length,
  };
}

// locationStatus says whether distance filters can place the contact: their
// city was found on the map, wasn't found (so distance filters skip them),
// or is still being looked up. Null when they have no city.
export function locationStatus(contact) {
  if (!(contact.city || "").trim()) return null;
  if (contact.lat != null) return { found: true, text: "On the map" };
  if (contact.geo_key != null && contact.geo_key === contact.geo_source) {
    return { found: false, text: "Not found on the map, so distance filters leave them out" };
  }
  return { found: false, text: "Looking it up…" };
}
